import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Clock, Phone, Instagram, MessageCircle } from "lucide-react";

export function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-primary/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">
              FIND US
            </p>
            <h2 className="section-heading text-foreground mb-8">
              LOCATION &<br />
              <span className="text-gradient-gold">CONTACT</span>
            </h2>

            <div className="space-y-6">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Address</h3>
                  <p className="text-muted-foreground">
                    123 Sukhumvit Road, Soi 11<br />
                    Khlong Toei, Bangkok 10110<br />
                    Thailand
                  </p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Opening Hours</h3>
                  <p className="text-muted-foreground">
                    Monday - Thursday: 5PM - 1AM<br />
                    Friday - Saturday: 5PM - 3AM<br />
                    Sunday: 5PM - 12AM
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                  <a
                    href="tel:+66212345678"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    +66 2 123 4567
                  </a>
                </div>
              </div>

              {/* Social */}
              <div className="flex gap-4 pt-4">
                <a
                  href="https://instagram.com/tommyhour"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5" aria-hidden="true" />
                </a>
                <a
                  href="https://wa.me/66812345678"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all"
                  aria-label="Contact us on WhatsApp"
                >
                  <MessageCircle className="w-5 h-5" aria-hidden="true" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-square lg:aspect-auto lg:h-full min-h-[400px] rounded-lg overflow-hidden glass">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.5386543!2d100.5553!3d13.7437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e29e1e2e0b9a5b%3A0x1234567890abcdef!2sSukhumvit%20Soi%2011!5e0!3m2!1sen!2sth!4v1700000000000!5m2!1sen!2sth"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tommy Hour Location"
              />
            </div>
            {/* Overlay card */}
            <div className="absolute bottom-6 left-6 right-6 glass p-4 rounded-lg">
              <p className="text-foreground font-medium">Tommy Hour Bar</p>
              <p className="text-muted-foreground text-sm">Sukhumvit Soi 11, Bangkok</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
